import { useMemo, useState } from 'react';

import { TEMPLATES } from '../charts/registry';
import type { CatalogChart, SavedChart, SavedDashboard, SavedWidget } from '../types';
import { ChartRenderer } from './ChartRenderer';

/**
 * "Test" a saved chart, dashboard or widget with the parameters the platform
 * would supply at view time — an app id, a store, a category — and see the
 * *real* compiled result the embed would draw.
 *
 * An author has none of that view-time context, so a chart that needs an
 * `app_id` shows nothing in its card. This closes the gap: enter the values,
 * and it runs the same `/api/query` the embed runs, with what you typed.
 *
 * The preview is also a light editor: flip the chart's form (bar, trend, table…)
 * or a widget's aspect ratio and see it re-draw immediately. Nothing here is
 * saved — it is a dry run. To change a chart permanently, or to build a custom
 * HTML / AI chart, use Edit, which is the full studio.
 */

export type TestTarget =
  | { kind: 'chart'; chart: SavedChart }
  | { kind: 'widget'; widget: SavedWidget }
  | { kind: 'dashboard'; dashboard: SavedDashboard };

interface Props {
  target: TestTarget;
  charts: SavedChart[];
  catalog: CatalogChart[];
  onClose: () => void;
}

/** A friendlier field than the raw parameter name. */
const FIELD: Record<string, { label: string; placeholder: string; hint?: string; store?: boolean }> = {
  app_id: { label: 'App ID', placeholder: 'com.whatsapp', hint: 'A store bundle id.' },
  app_ids: {
    label: 'App IDs',
    placeholder: 'com.whatsapp|com.spotify.music',
    hint: 'Pipe-separated — one app is a list of one.',
  },
  platform: { label: 'Store', placeholder: 'android', store: true },
  category: { label: 'Category', placeholder: 'Comics' },
  user_partner_id: {
    label: 'Partner ID',
    placeholder: '1',
    hint: 'The platform derives this from the viewer; supply one (or several, pipe-separated) only to test partner-scoped data. Honoured for signed-in authors only.',
  },
  from: { label: 'From', placeholder: '2026-01-01' },
  to: { label: 'To', placeholder: '2026-06-30' },
  symbol: { label: 'Symbol', placeholder: '' },
};

const STORE_OPTIONS = [
  { value: 'android', label: 'Google Play (android)' },
  { value: 'itunes', label: 'App Store (itunes)' },
];

const RATIOS = ['2/1', '16/9', '4/3', '1/1', '3/2', '3/4', '2/3', '4/5'];

/** When the catalogue can't tell us, offer the parameters most queries take. */
const FALLBACK_PARAMS = ['app_id', 'app_ids', 'platform', 'category'];

const savedChartById = (charts: SavedChart[], id: string): SavedChart | undefined =>
  charts.find((c) => c.id === id);

const catalogFor = (catalog: CatalogChart[], chart: SavedChart): CatalogChart | undefined =>
  catalog.find((c) => c.chartId === chart.chartId);

export const TestDialog = ({ target, charts, catalog, onClose }: Props) => {
  /** The chart at the centre of this test — the item itself, or a tile/widget's chart. */
  const primaryChart =
    target.kind === 'chart'
      ? target.chart
      : target.kind === 'widget'
        ? savedChartById(charts, target.widget.chartId)
        : undefined;

  /** Which parameters to offer, and which are required. */
  const { fields, required } = useMemo(() => {
    if (target.kind === 'dashboard') {
      const f =
        target.dashboard.appFilter === 'single'
          ? ['app_id']
          : target.dashboard.appFilter === 'multiple'
            ? ['app_ids']
            : [];
      return { fields: f, required: f };
    }
    const entry = primaryChart ? catalogFor(catalog, primaryChart) : undefined;
    // Every parameter the query accepts, so the author can also try optional
    // filters — falling back to the common set when the catalogue is unknown,
    // which is what stops this from ever opening with nothing to enter.
    const accepted = [...(entry?.params?.length ? entry.params : FALLBACK_PARAMS)];
    // "Actuals" charts are partner-scoped by the viewer's identity, which an
    // author doesn't have — so let the test supply a partner id to prove it works.
    if (entry && /^actuals\b/i.test(entry.title) && !accepted.includes('user_partner_id')) {
      accepted.push('user_partner_id');
    }
    return { fields: accepted, required: entry?.requiredParams ?? [] };
  }, [target, primaryChart, catalog]);

  // Pre-fill from whatever the item already carries, so a saved default shows.
  const [values, setValues] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = { ...(primaryChart?.params ?? {}) };
    if (fields.includes('platform') && !seed.platform) seed.platform = 'android';
    return seed;
  });

  // Live overrides for the preview — never saved.
  const [form, setForm] = useState<string>(primaryChart?.template ?? '');
  const [ratio, setRatio] = useState<string>(
    target.kind === 'widget' ? target.widget.aspectRatio : '16/9',
  );
  const [running, setRunning] = useState(false);

  const missing = required.filter((p) => !values[p]?.trim());

  const params = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) if (v.trim()) out[k] = v.trim();
    return out;
  }, [values]);

  const title =
    target.kind === 'chart'
      ? target.chart.name
      : target.kind === 'widget'
        ? primaryChart?.name ?? 'Widget'
        : target.dashboard.name;

  // The chart actually rendered, with the live form override applied.
  const renderChart: SavedChart | undefined = primaryChart
    ? { ...primaryChart, template: form || primaryChart.template }
    : undefined;

  return (
    <div className="test" role="dialog" aria-modal="true" aria-label={`Test ${title}`}>
      <div className="test__backdrop" onClick={onClose} />
      <div className="test__panel">
        <header className="test__head">
          <div>
            <span className="test__kind">Test {target.kind}</span>
            <h3>{title}</h3>
          </div>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {!running ? (
          <form
            className="test__form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!missing.length) setRunning(true);
            }}
          >
            <p className="hint">
              {fields.length
                ? 'Enter the parameters the platform supplies at view time, then compile the result.'
                : 'This item needs no parameters — run it to see the compiled result.'}
            </p>

            {fields.map((p) => {
              const f = FIELD[p] ?? { label: p, placeholder: '' };
              const isRequired = required.includes(p);
              return (
                <label className="test__field" key={p}>
                  <span>
                    {f.label}
                    {isRequired && <em className="test__req"> · required</em>}
                  </span>
                  {f.store ? (
                    <select
                      value={values[p] ?? 'android'}
                      onChange={(e) => setValues((v) => ({ ...v, [p]: e.target.value }))}
                    >
                      {STORE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={values[p] ?? ''}
                      placeholder={f.placeholder}
                      onChange={(e) => setValues((v) => ({ ...v, [p]: e.target.value }))}
                    />
                  )}
                  {f.hint && <span className="test__hint">{f.hint}</span>}
                </label>
              );
            })}

            <div className="test__actions">
              <button type="button" className="ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="primary" disabled={missing.length > 0}>
                Run test
              </button>
            </div>
          </form>
        ) : (
          <div className="test__result">
            <div className="test__runbar">
              <button className="ghost" onClick={() => setRunning(false)}>
                ← Parameters
              </button>

              {/* Live form picker — charts and widgets only; a dashboard's tiles
                  each carry their own form. */}
              {renderChart && (
                <label className="test__pick">
                  <span>Form</span>
                  <select value={form || renderChart.template} onChange={(e) => setForm(e.target.value)}>
                    {TEMPLATES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {target.kind === 'widget' && (
                <label className="test__pick">
                  <span>Ratio</span>
                  <select value={ratio} onChange={(e) => setRatio(e.target.value)}>
                    {RATIOS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {Object.keys(params).length > 0 && (
                <code className="test__params">
                  {Object.entries(params)
                    .map(([k, v]) => `${k}=${v}`)
                    .join('  ')}
                </code>
              )}
            </div>

            <TestResult target={target} charts={charts} params={params} renderChart={renderChart} ratio={ratio} />
          </div>
        )}
      </div>
    </div>
  );
};

const TestResult = ({
  target,
  charts,
  params,
  renderChart,
  ratio,
}: {
  target: TestTarget;
  charts: SavedChart[];
  params: Record<string, string>;
  renderChart?: SavedChart;
  ratio: string;
}) => {
  if (target.kind === 'chart') {
    if (!renderChart) return null;
    return <ChartRenderer chart={renderChart} overrideParams={params} />;
  }

  if (target.kind === 'widget') {
    if (!renderChart) return <p className="error">This widget points at a chart that no longer exists.</p>;
    return (
      <div className="test__widget" style={{ aspectRatio: ratio.replace('/', ' / ') }}>
        <ChartRenderer chart={renderChart} overrideParams={params} chrome={false} />
      </div>
    );
  }

  return (
    <div className="test__grid">
      {target.dashboard.tiles.map((tile, i) => {
        const chart = savedChartById(charts, tile.chartId);
        if (!chart) return null;
        return (
          <div
            className="test__tile"
            key={`${tile.chartId}-${i}`}
            style={{ gridColumn: `span ${Math.min(12, Math.max(1, tile.width))}` }}
          >
            <ChartRenderer chart={chart} overrideParams={params} title={tile.title ?? chart.name} />
          </div>
        );
      })}
    </div>
  );
};
